package com.sddamico.components.widgets

import androidx.compose.runtime.*
import com.sddamico.SitePalette
import com.sddamico.components.motion.Collapsible
import com.sddamico.api.apiGet
import com.sddamico.models.PlayerScoringResponse
import com.sddamico.models.PlayerUniverseEntry
import com.sddamico.util.formatPoints
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

@Composable
fun UniversePlayerCard(
    player: PlayerUniverseEntry,
    leagueYearId: String,
    isExpanded: Boolean = false,
    onToggleExpand: () -> Unit = {},
    onClaim: ((PlayerUniverseEntry) -> Unit)? = null,
    onTrade: (() -> Unit)? = null,
) {
    val ctx = rememberPageContext()
    val isOwned = player.currentClub != null
    val leftBorderColor = if (isOwned) SitePalette.primary else SitePalette.border

    val expanded = isExpanded
    var scoringData by remember(player.id) { mutableStateOf<PlayerScoringResponse?>(null) }
    var scoringLoading by remember(player.id) { mutableStateOf(false) }
    var scoringError by remember(player.id) { mutableStateOf<String?>(null) }

    // Fetch scoring data on first expand
    LaunchedEffect(expanded) {
        if (expanded && scoringData == null && !scoringLoading) {
            scoringLoading = true
            scoringError = null
            try {
                scoringData = apiGet(
                    "/api/clubs/player-scoring?playerId=${player.id}&leagueYearId=$leagueYearId",
                    PlayerScoringResponse.serializer()
                )
            } catch (e: Exception) {
                scoringError = "Failed to load scoring data"
            }
            scoringLoading = false
        }
    }

    Column(
        Modifier
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderLeft(3.px, LineStyle.Solid, leftBorderColor)
            .borderRadius(8.px)
            .padding(topBottom = 0.625.cssRem, leftRight = 0.875.cssRem)
            .fillMaxWidth()
            .cursor(Cursor.Pointer)
            .onClick { onToggleExpand() }
            .transition(Transition.of("background-color", 150.ms, TransitionTimingFunction.Ease))
    ) {
        // Mobile layout (<MD): 2-row card
        Column(Modifier.fillMaxWidth().displayUntil(Breakpoint.MD).gap(0.25.cssRem)) {
            // Row 1: Player name + age | Points | Chevron
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    SpanText(
                        player.name,
                        Modifier
                            .fontSize(0.9375.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .color(SitePalette.text)
                    )
                    if (player.age != null) {
                        val isUnderAge = player.age <= 25
                        SpanText(
                            "${player.age}",
                            Modifier
                                .fontSize(0.75.cssRem)
                                .color(if (isUnderAge) SitePalette.error else SitePalette.textMuted)
                                .margin(left = 0.375.cssRem)
                        )
                    }
                }
                Spacer()
                SpanText(
                    "${formatPoints(player.seasonPoints)} pts",
                    Modifier
                        .fontSize(0.9375.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .color(SitePalette.accent)
                        .styleModifier { property("font-variant-numeric", "tabular-nums") }
                )
                ExpandChevron(expanded, Modifier.fontSize(0.5625.cssRem).margin(left = 0.5.cssRem))
            }
            // Row 2: Sport badge + ranking | Club/Free Agent
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                SportBadge(player.sport, player.isLiv)
                if (player.rankingLabel != null) {
                    SpanText(
                        "${player.rankingLabel}: ",
                        Modifier.fontSize(0.75.cssRem).color(SitePalette.textMuted).margin(left = 0.625.cssRem)
                    )
                    SpanText(
                        "#${player.rankingValue}",
                        Modifier.fontSize(0.75.cssRem).color(SitePalette.text)
                    )
                }
                Spacer()
                if (isOwned) {
                    SpanText(
                        player.currentClub!!.name,
                        Modifier
                            .fontSize(0.8125.cssRem)
                            .fontWeight(FontWeight.Medium)
                            .color(SitePalette.primary)
                            .cursor(Cursor.Pointer)
                            .styleModifier { property("text-decoration", "none") }
                            .onClick {
                                it.stopPropagation()
                                ctx.router.tryRoutingTo("/clubhouse?clubId=${player.currentClub.id}")
                            }
                    )
                    if (onTrade != null) {
                        TradeIconButton(onClick = onTrade)
                    }
                } else {
                    val isUnderAge = player.age != null && player.age <= 25
                    val isWaiver = player.waiverEndDate != null
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        if (isUnderAge) {
                            SpanText(
                                "Under 25",
                                Modifier
                                    .fontSize(0.8125.cssRem)
                                    .fontWeight(FontWeight.Medium)
                                    .color(SitePalette.error)
                            )
                        } else {
                            Div(attrs = {
                                if (isWaiver) title("Waivers end: ${formatWaiverEndDate(player.waiverEndDate!!)}")
                            }) {
                                SpanText(
                                    if (isWaiver) "Waivers" else "Free Agent",
                                    Modifier
                                        .fontSize(0.8125.cssRem)
                                        .fontWeight(FontWeight.Normal)
                                        .color(SitePalette.textMuted)
                                )
                            }
                            if (onClaim != null) {
                                Div(
                                    Modifier
                                        .size(1.25.cssRem)
                                        .borderRadius(9999.px)
                                        .backgroundColor(if (isWaiver) SitePalette.accent else SitePalette.primary)
                                        .display(DisplayStyle.Flex)
                                        .alignItems(AlignItems.Center)
                                        .justifyContent(JustifyContent.Center)
                                        .cursor(Cursor.Pointer)
                                        .margin(left = 0.375.cssRem)
                                        .onClick {
                                            it.stopPropagation()
                                            onClaim(player)
                                        }
                                        .toAttrs()
                                ) {
                                    SpanText(
                                        "+",
                                        Modifier
                                            .fontSize(0.875.cssRem)
                                            .fontWeight(FontWeight.Bold)
                                            .color(com.varabyte.kobweb.compose.ui.graphics.Colors.White)
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }

        // Desktop layout (MD+): Single row
        Row(
            Modifier.fillMaxWidth().displayIfAtLeast(Breakpoint.MD),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Player name + age — fixed width column
            Row(
                Modifier.styleModifier { property("min-width", "12rem") },
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    player.name,
                    Modifier
                        .fontSize(0.9375.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .color(SitePalette.text)
                )
                if (player.age != null) {
                    val isUnderAge = player.age <= 25
                    SpanText(
                        "${player.age}",
                        Modifier
                            .fontSize(0.75.cssRem)
                            .color(if (isUnderAge) SitePalette.error else SitePalette.textMuted)
                            .margin(left = 0.375.cssRem)
                    )
                }
            }

            // Sport badge + ranking
            SportBadge(player.sport, player.isLiv)
            if (player.rankingLabel != null) {
                SpanText(
                    "${player.rankingLabel}: ",
                    Modifier.fontSize(0.75.cssRem).color(SitePalette.textMuted).margin(left = 0.625.cssRem)
                )
                SpanText(
                    "#${player.rankingValue}",
                    Modifier.fontSize(0.75.cssRem).color(SitePalette.text)
                )
            }

            Spacer()

            // Club name or Free Agent
            if (isOwned) {
                SpanText(
                    player.currentClub!!.name,
                    Modifier
                        .fontSize(0.8125.cssRem)
                        .fontWeight(FontWeight.Medium)
                        .color(SitePalette.primary)
                        .cursor(Cursor.Pointer)
                        .styleModifier {
                            property("min-width", "7rem")
                            property("text-align", "right")
                            property("text-decoration", "none")
                        }
                        .onClick {
                            it.stopPropagation()
                            ctx.router.tryRoutingTo("/clubhouse?clubId=${player.currentClub.id}")
                        }
                )
                if (onTrade != null) {
                    Div(
                        Modifier
                            .size(1.25.cssRem)
                            .borderRadius(9999.px)
                            .border(1.px, LineStyle.Solid, SitePalette.accent)
                            .display(DisplayStyle.Flex)
                            .styleModifier {
                                property("align-items", "center")
                                property("justify-content", "center")
                            }
                            .cursor(Cursor.Pointer)
                            .margin(left = 0.375.cssRem)
                            .onClick { it.stopPropagation(); onTrade() }
                            .toAttrs()
                    ) {
                        SpanText("⇄", Modifier.fontSize(0.625.cssRem).color(SitePalette.accent))
                    }
                }
            } else {
                val isUnderAge = player.age != null && player.age <= 25
                val isWaiver = player.waiverEndDate != null
                Row(
                    Modifier.styleModifier {
                        property("min-width", "7rem")
                        property("justify-content", "flex-end")
                    },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (isUnderAge) {
                        SpanText(
                            "Under 25",
                            Modifier
                                .fontSize(0.8125.cssRem)
                                .fontWeight(FontWeight.Medium)
                                .color(SitePalette.error)
                        )
                    } else {
                        Div(
                            Modifier
                                .title(if (isWaiver) "Waivers end: ${formatWaiverEndDate(player.waiverEndDate!!)}" else "")
                                .toAttrs()
                        ) {
                            SpanText(
                                if (isWaiver) "Waivers" else "Free Agent",
                                Modifier
                                    .fontSize(0.8125.cssRem)
                                    .fontWeight(FontWeight.Normal)
                                    .color(SitePalette.textMuted)
                            )
                        }
                        if (onClaim != null) {
                            Div(
                                Modifier
                                    .size(1.25.cssRem)
                                    .borderRadius(9999.px)
                                    .backgroundColor(if (isWaiver) SitePalette.accent else SitePalette.primary)
                                    .display(DisplayStyle.Flex)
                                    .alignItems(AlignItems.Center)
                                    .justifyContent(JustifyContent.Center)
                                    .cursor(Cursor.Pointer)
                                    .margin(left = 0.375.cssRem)
                                    .onClick {
                                        it.stopPropagation()
                                        onClaim(player)
                                    }
                                    .toAttrs()
                            ) {
                                SpanText(
                                    "+",
                                    Modifier
                                        .fontSize(0.875.cssRem)
                                        .fontWeight(FontWeight.Bold)
                                        .color(com.varabyte.kobweb.compose.ui.graphics.Colors.White)
                                )
                            }
                        }
                    }
                }
            }

            // Season points
            SpanText(
                "${formatPoints(player.seasonPoints)} pts",
                Modifier
                    .fontSize(0.9375.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.accent)
                    .margin(left = 1.cssRem)
                    .styleModifier {
                        property("font-variant-numeric", "tabular-nums")
                        property("min-width", "5rem")
                        property("text-align", "right")
                    }
            )

            // Chevron indicator
            ExpandChevron(expanded, Modifier.fontSize(0.5625.cssRem).margin(left = 0.75.cssRem))
        }

        Collapsible(expanded) {
            ScoringSummary(scoringLoading, scoringError, scoringData, topSpacing = 0.625.cssRem)
        }
    }
}

internal fun formatWaiverEndDate(isoDateTime: String): String {
    // Input: "2026-03-03T05:00" — format as "Tue, Mar 3 at 5:00 AM ET"
    return try {
        val date = kotlin.js.Date(isoDateTime)
        val days = arrayOf("Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat")
        val months = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
        val dayName = days[date.getDay()]
        val month = months[date.getMonth()]
        val day = date.getDate()
        val hour = date.getHours()
        val minute = date.getMinutes()
        val ampm = if (hour < 12) "AM" else "PM"
        val displayHour = if (hour == 0) 12 else if (hour > 12) hour - 12 else hour
        val displayMinute = if (minute < 10) "0$minute" else "$minute"
        "$dayName, $month $day at $displayHour:$displayMinute $ampm ET"
    } catch (_: Exception) {
        isoDateTime
    }
}

@Composable
private fun TradeIconButton(onClick: () -> Unit) {
    Div(
        Modifier
            .size(1.25.cssRem)
            .borderRadius(9999.px)
            .border(1.px, LineStyle.Solid, SitePalette.accent)
            .display(DisplayStyle.Flex)
            .styleModifier {
                property("align-items", "center")
                property("justify-content", "center")
            }
            .cursor(Cursor.Pointer)
            .margin(left = 0.375.cssRem)
            .onClick { it.stopPropagation(); onClick() }
            .toAttrs()
    ) {
        SpanText("⇄", Modifier.fontSize(0.625.cssRem).color(SitePalette.accent))
    }
}
